/**
 * Validation helper
 */
import validateJS, { ValidateOption } from 'validate.js';

import { DeliveryServiceType } from '../types/delivery.types';
import i18n from './i18n';

/**
 * Validates shipping object
 */
validateJS.validators.validateShipping = (shipping: DeliveryServiceType, options) => {
  const shippingKeys = Object.keys(shipping) || [];
  const selectedShippingOptionName = shippingKeys.find((shippingKey) => shipping[shippingKey]?.enabled);
  const atLeastOneEnabled = !!selectedShippingOptionName && !!shipping?.[selectedShippingOptionName]?.selectedPrice;

  // Check that at least one option is selected
  if (options.atLeastOneSelected && (!shippingKeys.length || !atLeastOneEnabled)) {
    return i18n.t('components.product-edit.errors.no-shipping');
  }
};

validateJS.validators.validateMtaaniPhone = (phone: string) => {
  const MTAANI_PHONE_REGEX = /^[+]?(254|0)(1|7)[0-9]{8}$/;

  if (!phone?.match(MTAANI_PHONE_REGEX)) {
    return i18n.t('components.product-edit.errors.mtaani-phone-format');
  }
};
/**
 * Validates Omniva phone for both Estonian and Latvian numbers
 */

validateJS.validators.validateOmnivaPhone = (phone: unknown, options) => {
  if (typeof phone !== 'string') {
    return i18n.t('pages.buy.errors.phone');
  }

  // Detect country from phone number
  const isEstonian = /^(\+372|00372|372)/.test(phone);
  const isLatvian = /^(\+371|00371|371)/.test(phone);

  if (isEstonian) {
    // Estonian validation (existing logic)
    const nationalNumber = phone.replace(/^(\+372|00372|372)/, '');
    const phoneLength = nationalNumber.length;
    const startsFive = nationalNumber.startsWith('5');
    const startsEight = nationalNumber.startsWith('8');

    if (!(startsFive || startsEight)) {
      return i18n.t('pages.buy.errors.shipping.phoneShouldStart', {
        startChar1: 5,
        startChar2: 8,
      });
    }

    if (phoneLength === options.allowedLengthForFive && !startsFive) {
      return i18n.t('pages.buy.errors.shipping.allowedForNumberLength', {
        phoneLength: options.allowedLengthForFive,
        startNumber: 5,
      });
    }

    if (startsFive && ![options.allowedLengthForFive, options.allowedLength].includes(phoneLength)) {
      return i18n.t('pages.buy.errors.shipping.allowedForNumberCondition', {
        phoneLength: options.allowedLengthForFive,
        phoneLengthOr: options.allowedLength,
      });
    }

    if (startsEight && phoneLength !== options.allowedLength) {
      return i18n.t('pages.buy.errors.shipping.allowedLength', {
        phoneLength: options.allowedLength,
      });
    }
  } else if (isLatvian) {
    // Latvian validation
    const nationalNumber = phone.replace(/^(\+371|00371|371)/, '');
    const phoneLength = nationalNumber.length;
    const startsTwo = nationalNumber.startsWith('2');
    const startsSix = nationalNumber.startsWith('6');
    const startsSeven = nationalNumber.startsWith('7');
    const startsNine = nationalNumber.startsWith('9');

    // Latvian mobile numbers start with 2, 6, 7, or 9
    if (!(startsTwo || startsSix || startsSeven || startsNine)) {
      return i18n.t('pages.buy.errors.shipping.phoneShouldStart', {
        startChar1: '2, 6, 7',
        startChar2: 9,
      });
    }

    // Latvian mobile numbers should be exactly 8 digits
    if (phoneLength !== 8) {
      return i18n.t('pages.buy.errors.shipping.allowedLength', {
        phoneLength: 8,
      });
    }
  } else {
    // Fallback to Estonian validation for backward compatibility (no country code detected)
    const nationalNumber = phone.replace(/^(\+372|00372|372)/, '');
    const phoneLength = nationalNumber.length;
    const startsFive = nationalNumber.startsWith('5');
    const startsEight = nationalNumber.startsWith('8');

    if (!(startsFive || startsEight)) {
      return i18n.t('pages.buy.errors.shipping.phoneShouldStart', {
        startChar1: 5,
        startChar2: 8,
      });
    }

    if (phoneLength === options.allowedLengthForFive && !startsFive) {
      return i18n.t('pages.buy.errors.shipping.allowedForNumberLength', {
        phoneLength: options.allowedLengthForFive,
        startNumber: 5,
      });
    }

    if (startsFive && ![options.allowedLengthForFive, options.allowedLength].includes(phoneLength)) {
      // eslint-disable-next-line max-len
      return i18n.t('pages.buy.errors.shipping.allowedForNumberCondition', {
        phoneLength: options.allowedLengthForFive,
        phoneLengthOr: options.allowedLength,
      });
    }

    if (startsEight && phoneLength !== options.allowedLength) {
      return i18n.t('pages.buy.errors.shipping.allowedLength', {
        phoneLength: options.allowedLength,
      });
    }
  }
};

validateJS.validators.validateInternationalAndCountryPhone = (phone, options) => {
  const { message } = options;
  const regex = /^[+]?([6789]?|[0-9]{1,3}?)[0-9]{9,12}?$/im;

  if (!regex.test(phone)) {
    return message ? message : i18n.t('pages.buy.errors.international-phone');
  }
};

/**
 * India PINcodes are always 6 chars long.
 * The first two number define the region and this function allows to only allow certain regions
 *
 */
validateJS.validators.validateIndiaPinCode = (pinCode, options) => {
  const pinCodeLength = pinCode?.length;
  const pinCodeRegion = pinCode?.substring(0, 2);

  if (pinCodeLength !== 6) {
    return i18n.t('pages.buy.errors.shipping.delhiveryInvalidPin');
  }

  if (options.allowedRegions && options.allowedRegions.length && !options.allowedRegions.includes(+pinCodeRegion)) {
    return i18n.t('pages.buy.errors.shipping.delhiveryInvalidPinRegion', {
      allowedRegions: options.allowedRegions.join(', '),
    });
  }
};
/**
 *
 * @param data - any object
 * @param constraints - any object - matching rules for data object
 * @param options
 */
// eslint-disable-next-line import/prefer-default-export
export default function validate(data: any, constraints: any, options: ValidateOption = { fullMessages: false }) {
  const errors = validateJS(data, constraints, options) || {};

  return {
    hasErrors: Object.keys(errors).some((key) => errors[key].length),
    errors,
  };
}
